import { useState, useEffect } from "react";
import { X, Clock, Sparkles, CheckCircle2, Mail, User, Phone, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ALL_LOCATIONS } from "@/lib/locations";
import { cn } from "@/lib/utils";
import { useLocation as useRouterLocation } from "react-router-dom";

const LOCATION_ID = "M7JmlFFXdtFNLwyfY61D";
const TRACKING_ID = "tk_12d30557e33f4e29be39cf7c348f6d99";
const VIBE_API_URL = "https://backend.leadconnectorhq.com/vibe-ai";

// Custom field registered for waitlist area
const WAITLIST_AREA_FIELD = { id: "UYBRwjZvFNQWVeBs4FHF", key: "contact.waitlist_area" };

type Step = "banner" | "form" | "success";

export const WaitlistPopup = () => {
  const routerLocation = useRouterLocation();
  const path = routerLocation.pathname;
  const [step, setStep] = useState<Step>("banner");
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEs = routerLocation.pathname.startsWith('/es');
  const isRu = routerLocation.pathname.startsWith('/ru');
  const isZh = routerLocation.pathname.startsWith('/zh');

  const t = (en: string, es: string, ru: string, zh: string) => 
    isEs ? es : isRu ? ru : isZh ? zh : en;

  // Determine location from URL
  let resolvedLocation = { name: "Sacramento", regionSlug: "sacramento-area" };
  const pathParts = path.split('/').filter(Boolean);
  const lastPart = pathParts[pathParts.length - 1];

  if (path.includes('/los-angeles') || path.includes('/la/')) resolvedLocation = { name: "Los Angeles", regionSlug: "los-angeles" };
  else if (path.includes('/san-francisco')) resolvedLocation = { name: "San Francisco", regionSlug: "san-francisco" };
  else if (path.includes('/san-diego')) resolvedLocation = { name: "San Diego", regionSlug: "san-diego" };
  else if (path.includes('/orange-county') || path.includes('/irvine') || path.includes('/anaheim') || path.includes('/huntington-beach') || path.includes('/santa-ana')) resolvedLocation = { name: "Orange County", regionSlug: "orange-county" };
  else if (path.includes('/santa-clara') || path.includes('/san-jose') || path.includes('/silicon-valley')) resolvedLocation = { name: "Santa Clara", regionSlug: "santa-clara" };
  else if (path.includes('/alameda') || path.includes('/oakland') || path.includes('/berkeley') || path.includes('/fremont') || path.includes('/hayward') || path.includes('/pleasanton') || path.includes('/dublin') || path.includes('/livermore') || path.includes('/tri-valley') || path.includes('/east-bay')) resolvedLocation = { name: "Alameda County", regionSlug: "alameda" };
  else if (path.includes('/contra-costa') || path.includes('/walnut-creek') || path.includes('/san-ramon')) resolvedLocation = { name: "Contra Costa", regionSlug: "contra-costa" };
  else if (path.includes('/marin')) resolvedLocation = { name: "Marin County", regionSlug: "marin" };
  else if (path.includes('/solano') || path.includes('/vallejo') || path.includes('/fairfield') || path.includes('/vacaville') || path.includes('/benicia')) resolvedLocation = { name: "Solano County", regionSlug: "solano" };
  else {
     const loc = ALL_LOCATIONS.find(l => l.slug === lastPart);
     if (loc) resolvedLocation = { name: loc.name, regionSlug: loc.regionSlug };
  }

  // Only show for non-Sacramento areas
  const isSacramento = resolvedLocation.regionSlug === "sacramento-area";

  useEffect(() => {
    const key = `waitlist_banner_dismissed_${resolvedLocation.regionSlug}`;
    if (sessionStorage.getItem(key)) setDismissed(true);
  }, [resolvedLocation.regionSlug]);

  if (isSacramento || dismissed) return null;

  const handleDismiss = () => {
    const key = `waitlist_banner_dismissed_${resolvedLocation.regionSlug}`;
    sessionStorage.setItem(key, "1");
    setDismissed(true);
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.firstName.trim()) e.firstName = t("Required", "Requerido", "Обязательно", "必填");
    if (!form.lastName.trim()) e.lastName = t("Required", "Requerido", "Обязательно", "必填");
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = t("Valid email required", "Correo válido requerido", "Требуется действительный email", "请输入有效的邮箱");
    if (!form.phone.trim()) e.phone = t("Required", "Requerido", "Обязательно", "必填");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    const sessionId = crypto.randomUUID();

    try {
      // Submit to CRM via vibe booking endpoint (used as contact creation)
      await fetch(`${VIBE_API_URL}/booking/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          locationId: LOCATION_ID,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          notes: `Waitlist request for ${resolvedLocation.name}. Submitted from ${resolvedLocation.name} service area page.`,
          sessionId,
          customFields: [
            { id: WAITLIST_AREA_FIELD.id, key: WAITLIST_AREA_FIELD.key, field_value: resolvedLocation.name }
          ]
        })
      }).catch(() => {});

      // Fire-and-forget CRM tracking
      fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
        method: "POST",
        headers: { "Content-Type": "application/json", version: "2021-07-28" },
        body: JSON.stringify({
          type: "external_form_submission",
          timestamp: Date.now(),
          formId: "Waitlist Form",
          formData: {
            first_name: form.firstName,
            last_name: form.lastName,
            email: form.email,
            phone: form.phone,
            [WAITLIST_AREA_FIELD.key]: resolvedLocation.name,
          },
          formLabels: {
            first_name: "First Name",
            last_name: "Last Name",
            email: "Email",
            phone: "Phone",
            [WAITLIST_AREA_FIELD.key]: "Waitlist Area",
          },
          url: window.location.href,
          title: document.title,
          path: window.location.pathname,
          userAgent: navigator.userAgent,
          trackingId: TRACKING_ID,
          locationId: LOCATION_ID,
          sessionId,
          properties: {
            deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? "mobile" : "desktop",
          },
        })
      }).catch(() => {});

      setStep("success");
    } catch {
      // Still show success to user
      setStep("success");
    } finally {
      setLoading(false);
    }
  };

  // ── BANNER (collapsed state) ──────────────────────────────────────────────
  if (step === "banner") {
    return (
      <>
        {/* Waitlist bar — sits BELOW the primary StickyBanner so they don't overlap */}
        <div className="fixed top-[40px] left-0 right-0 z-[55] animate-in slide-in-from-top-2 duration-500">
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 text-white px-4 py-2.5 shadow-xl">
            <div className="container flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <Clock className="h-4 w-4 text-white" />
                </div>
                <p className="text-sm font-semibold leading-snug">
                  <span className="font-extrabold">{t(`Limited Availability in ${resolvedLocation.name}:`, `Disponibilidad Limitada en ${resolvedLocation.name}:`, `Ограниченная доступность в ${resolvedLocation.name}:`, `${resolvedLocation.name}名额有限：`)}</span>{" "}
                  {t("We're at capacity but opening new spots this summer.", "Estamos al límite de capacidad pero abriremos nuevos espacios este verano.", "Мест нет, но мы откроем новые этим летом.", "目前已满，但今年夏天将开放新名额。")}{" "}
                  <button
                    onClick={() => setStep("form")}
                    className="underline underline-offset-2 font-extrabold hover:no-underline transition-all whitespace-nowrap"
                  >
                    {t("Join the Priority Waitlist →", "Únete a la Lista de Espera →", "Присоединиться к списку ожидания →", "加入优先候补名单 →")}
                  </button>
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Button
                  onClick={() => setStep("form")}
                  size="sm"
                  className="bg-white text-orange-600 hover:bg-white/90 font-extrabold text-xs h-8 px-4 rounded-full shadow-md hidden sm:flex"
                >
                  {t("Reserve My Spot", "Reservar mi Espacio", "Забронировать место", "预留我的位置")}
                </Button>
                <button
                  onClick={handleDismiss}
                  className="text-white/70 hover:text-white transition-colors p-1"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Floating popup card */}
        <div className="fixed bottom-24 right-4 z-50 max-w-xs w-full animate-in slide-in-from-bottom-4 fade-in duration-500 delay-1000 hidden md:block">
          <div className="bg-card border border-orange-200 rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 relative">
              <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-5 w-5 text-white" />
                <span className="text-white font-extrabold text-sm uppercase tracking-wide">{t("Exclusive Waitlist", "Lista de Espera Exclusiva", "Эксклюзивный список ожидания", "独家候补名单")}</span>
              </div>
              <p className="text-white/90 text-xs leading-snug">
                {t(`New spots opening in ${resolvedLocation.name} — Early Summer 2026`, `Nuevos espacios en ${resolvedLocation.name} — Principios de Verano 2026`, `Новые места в ${resolvedLocation.name} — Начало лета 2026`, `${resolvedLocation.name}新名额开放 — 2026年初夏`)}
              </p>
            </div>

            {/* Body */}
            <div className="p-5">
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {t("We're currently at capacity in", "Actualmente estamos al límite en", "В настоящее время нет свободных мест в", "目前在")}{" "}
                <strong className="text-foreground">{resolvedLocation.name}</strong>.{" "}
                {t("Join our priority waitlist and be the first to know when spots open — waitlist members get", "Únete a nuestra lista de espera y sé el primero en saber cuándo abramos espacios. ¡Los miembros obtienen", "Присоединяйтесь к приоритетному списку ожидания и узнайте первыми об открытии новых мест — участники списка получают", "加入优先候补名单，第一时间获取新名额通知 — 候补成员可享受")}{" "}
                <span className="text-orange-500 font-bold">{t("15% off", "15% de descuento", "скидку 15%", "15%折扣")}</span>{" "}
                {t("their first clean!", "en su primera limpieza!", "на первую уборку!", "首次清洁优惠！")}
              </p>
              <ul className="space-y-2 mb-5">
                {[
                  t("First access to new openings", "Acceso anticipado a nuevos espacios", "Ранний доступ к новым местам", "优先获取新名额"), 
                  t("Exclusive 15% welcome discount", "Descuento exclusivo del 15%", "Эксклюзивная скидка 15%", "独家15%迎新折扣"), 
                  t("No commitment required", "Sin compromiso", "Без обязательств", "无需承诺")
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs font-semibold text-foreground">
                    <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => setStep("form")}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 font-bold rounded-xl h-11 shadow-lg"
              >
                {t("Reserve My Spot", "Reservar mi Espacio", "Забронировать место", "预留我的位置")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── FORM MODAL ────────────────────────────────────────────────────────────
  if (step === "form") {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
        <div className="relative w-full max-w-md bg-card rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
          {/* Gradient top bar */}
          <div className="h-2 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600" />

          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-1.5 rounded-full bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close form"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>

          <div className="p-8">
            {/* Icon + Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-orange-100 mb-4">
                <Clock className="h-7 w-7 text-orange-500" />
              </div>
              <h2 className="text-2xl font-extrabold tracking-tight mb-2">{t(`Join the ${resolvedLocation.name} Waitlist`, `Únete a la Lista de Espera de ${resolvedLocation.name}`, `Присоединиться к списку ожидания в ${resolvedLocation.name}`, `加入 ${resolvedLocation.name} 候补名单`)}</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {t("We're fully booked in", "Estamos completamente reservados en", "Все места забронированы в", "我们在")} <strong>{resolvedLocation.name}</strong> {t("right now, but exciting things are coming. Reserve your spot and get", "ahora mismo, pero vienen cosas emocionantes. Reserva tu espacio y obtén", "прямо сейчас, но скоро будут новости. Забронируйте место и получите", "目前已满，但好消息即将到来。预留您的位置并在我们开放时获取")}{" "}
                <span className="text-orange-500 font-bold">{t("15% off", "15% de descuento", "скидку 15%", "15%折扣")}</span> {t("when we open — early summer 2026!", "cuando abramos — ¡principios de verano 2026!", "когда мы откроемся — начало лета 2026!", "— 2026年初夏！")}
              </p>
            </div>

            {/* Perks */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { icon: <Sparkles className="h-4 w-4" />, label: t("15% Off", "15% Dto", "Скидка 15%", "15%折扣") },
                { icon: <Clock className="h-4 w-4" />, label: t("First Access", "Acceso VIP", "Ранний доступ", "优先获取") },
                { icon: <CheckCircle2 className="h-4 w-4" />, label: t("No Commitment", "Sin Compromiso", "Без обязательств", "无承诺") },
              ].map((p, i) => (
                <div key={i} className="flex flex-col items-center gap-1 bg-orange-50 rounded-xl p-3 text-orange-600">
                  {p.icon}
                  <span className="text-xs font-bold text-center leading-tight">{p.label}</span>
                </div>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t("First Name", "Nombre", "Имя", "名字")}
                      className={cn("pl-9 h-11 rounded-xl", errors.firstName && "border-destructive")}
                      value={form.firstName}
                      onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                    />
                  </div>
                  {errors.firstName && <p className="text-xs text-destructive mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t("Last Name", "Apellido", "Фамилия", "姓氏")}
                      className={cn("pl-9 h-11 rounded-xl", errors.lastName && "border-destructive")}
                      value={form.lastName}
                      onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                    />
                  </div>
                  {errors.lastName && <p className="text-xs text-destructive mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder={t("Email Address", "Correo Electrónico", "Email", "电子邮箱")}
                    className={cn("pl-9 h-11 rounded-xl", errors.email && "border-destructive")}
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  />
                </div>
                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
              </div>

              <div>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="tel"
                    placeholder={t("Phone Number", "Número de Teléfono", "Номер телефона", "电话号码")}
                    className={cn("pl-9 h-11 rounded-xl", errors.phone && "border-destructive")}
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
              </div>

              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={resolvedLocation.name}
                  readOnly
                  className="pl-9 h-11 rounded-xl bg-muted/50 text-muted-foreground cursor-not-allowed"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-bold rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-lg shadow-orange-200 mt-2"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("Reserving your spot…", "Reservando tu espacio…", "Бронируем место…", "正在预留您的位置...")}</>
                ) : (
                  <>{t("Reserve My Spot", "Reservar mi Espacio", "Забронировать место", "预留我的位置")} <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground pt-1">
                {t("We'll email you at", "Te enviaremos un correo a", "Мы напишем вам на", "我们将通过邮件")} <strong>support@uplevelcleaning.com</strong> {t("when your spot is ready. No spam, ever.", "cuando tu espacio esté listo. Sin spam, nunca.", "когда ваше место будет готово. Никакого спама.", "通知您。绝无垃圾邮件。")}
              </p>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── SUCCESS STATE ─────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="relative w-full max-w-sm bg-card rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 text-center">
        <div className="h-2 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600" />
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1.5 rounded-full bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close success message"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
        <div className="p-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-extrabold mb-3">{t("You're on the List!", "¡Estás en la lista!", "Вы в списке!", "您已成功加入名单！")}</h2>
          <p className="text-muted-foreground text-sm leading-relaxed mb-6">
            {t("We've saved your spot in", "Hemos guardado tu espacio en", "Мы сохранили ваше место в", "我们已为您预留了在")} <strong className="text-foreground">{resolvedLocation.name}</strong>. {t("You'll be among the very first to know when we open new availability — and your exclusive", "Serás de los primeros en saber cuando abramos nueva disponibilidad — y tu", "Вы первыми узнаете о появлении свободных мест — и ваша эксклюзивная", "的位置。当有新名额时，您将第一时间获得通知 — 并且您的独家")}{" "}
            <span className="text-orange-500 font-bold">{t("15% welcome discount", "descuento de bienvenida del 15%", "приветственная скидка 15%", "15%迎新折扣")}</span> {t("will be waiting for you.", "te estará esperando.", "будет ждать вас.", "将为您保留。")}
          </p>
          <div className="bg-orange-50 rounded-2xl p-4 mb-6 border border-orange-100">
            <p className="text-xs text-orange-700 font-semibold">{t("Expected opening: Early Summer 2026", "Apertura esperada: Principios de Verano 2026", "Ожидаемое открытие: Начало лета 2026", "预计开放时间：2026年初夏")}</p>
          </div>
          <Button
            onClick={handleDismiss}
            className="w-full font-bold rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
          >
            {t("Continue Browsing", "Continuar Navegando", "Продолжить просмотр", "继续浏览")}
          </Button>
        </div>
      </div>
    </div>
  );
};
